import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import {
  AuditStatusChip,
  HeaderTip,
  ManualProgress,
  ReadinessBar,
} from "./AuditCells";
import { EditAccountTablePagination } from "../../../../components/AccountDetailPolicy/editAccountTableControls";
import {
  getComplianceFrameworkBadgeText,
  getComplianceIcon,
} from "../../../../assets/images/compliance/IconCompliance.jsx";
import { getAudit, unwrapGet } from "../../../../helpers/auditHub_helper";
import { APPROACHING_COMPLETION_DAYS, AUDIT_STATUS_LABEL, AUDIT_STATUSES } from "../constants";
import { AH_BORDER, AH_MUTED, AH_TEXT, AH_WHITE, searchFieldSx } from "../theme";
import {
  daysRemainingTone,
  formatDateTime,
  formatDaysRemaining,
  formatShortDate,
  frameworkLabel,
  scorePercent,
} from "../utils";

const headSx = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: AH_MUTED,
  backgroundColor: AH_WHITE,
  borderBottom: `1px solid ${AH_BORDER}`,
  whiteSpace: "normal",
  lineHeight: 1.25,
  py: 1.25,
  px: 0.75,
  textAlign: "center",
  overflow: "hidden",
};

/** Same body type as CSPM / Configuration tables. */
const cellSx = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.4,
  color: "#334155",
  borderBottom: `1px solid ${AH_BORDER}`,
  py: 1.5,
  verticalAlign: "middle",
  textAlign: "center",
  overflow: "hidden",
};

const bodyTextSx = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.4,
  color: "#334155",
  textAlign: "center",
};

const wrapTextSx = {
  ...bodyTextSx,
  display: "block",
  maxWidth: "100%",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const ACTION_BTN_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  color: "#64748b",
  padding: 0,
  flexShrink: 0,
  "&:hover": {
    backgroundColor: "#f1f5f9",
    color: "#334155",
  },
  "&.Mui-disabled": {
    color: "#cbd5e1",
    borderColor: "#f1f5f9",
    backgroundColor: "#f8fafc",
  },
};

const DELETE_BTN_SX = {
  ...ACTION_BTN_SX,
  color: "#dc2626",
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  "&:hover": {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
};

const COMPLETE_BTN_SX = {
  ...ACTION_BTN_SX,
  color: "#16a34a",
  borderColor: "#bbf7d0",
  backgroundColor: "#f0fdf4",
  "&:hover": {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
};

const EmptyRows = ({ message, colSpan = 10 }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ py: 6, borderBottom: 0 }}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", color: AH_MUTED }}>
        <SearchOffIcon sx={{ fontSize: 40, mb: 1, color: "#cbd5e1" }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: AH_TEXT }}>{message}</Typography>
      </Box>
    </TableCell>
  </TableRow>
);

const ActivityButton = ({ audit, onActivity }) => (
  <Tooltip title="Activity Trail">
    <IconButton size="small" onClick={() => onActivity(audit)} aria-label={`Activity trail ${audit.auditCode}`} sx={ACTION_BTN_SX}>
      <HistoryOutlinedIcon sx={{ fontSize: 15 }} />
    </IconButton>
  </Tooltip>
);

const CellTip = ({ title, children }) => (
  <Tooltip title={title == null ? "" : String(title)} placement="top" disableInteractive>
    <Box component="span" sx={{ display: "block", maxWidth: "100%", minWidth: 0 }}>
      {children}
    </Box>
  </Tooltip>
);

/**
 * The list summary carries no framework, so each visible row reads it from the assessment detail,
 * a few at a time. Keyed by the row object: a refreshed list brings new rows and so a fresh read.
 */
const DETAIL_CONCURRENCY = 4;
const detailQueue = [];
let detailActive = 0;

const runLimited = (task) =>
  new Promise((resolve, reject) => {
    const run = () => {
      detailActive += 1;
      task()
        .then(resolve, reject)
        .finally(() => {
          detailActive -= 1;
          const next = detailQueue.shift();
          if (next) next();
        });
    };
    if (detailActive < DETAIL_CONCURRENCY) run();
    else detailQueue.push(run);
  });

const frameworkRequests = new WeakMap();

const loadFrameworkNames = (audit) => {
  if (!frameworkRequests.has(audit)) {
    const request = runLimited(() => getAudit(audit.auditId)).then((res) => {
      const detail = unwrapGet(res);
      const names = new Set();
      (Array.isArray(detail?.accounts) ? detail.accounts : []).forEach((account) => {
        (Array.isArray(account.scopes) ? account.scopes : []).forEach((scope) => {
          if (scope?.framework) names.add(frameworkLabel(scope.framework));
        });
      });
      return [...names];
    });
    request.catch(() => frameworkRequests.delete(audit));
    frameworkRequests.set(audit, request);
  }
  return frameworkRequests.get(audit);
};

const FRAMEWORK_MARK_SIZE = 18;

/** Same logo source as the compliance pages; frameworks without a logo show their short badge text. */
const FrameworkMark = ({ name }) => {
  const badgeText = name ? getComplianceFrameworkBadgeText(name) : null;
  const iconSrc = name && !badgeText ? getComplianceIcon(name) : null;
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        width: FRAMEWORK_MARK_SIZE,
        height: FRAMEWORK_MARK_SIZE,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {iconSrc ? (
        <Box
          component="img"
          src={iconSrc}
          alt=""
          sx={{ maxWidth: FRAMEWORK_MARK_SIZE, maxHeight: FRAMEWORK_MARK_SIZE, objectFit: "contain" }}
        />
      ) : badgeText ? (
        <Typography component="span" sx={{ fontSize: "0.5rem", fontWeight: 700, lineHeight: 1, color: AH_TEXT }}>
          {badgeText}
        </Typography>
      ) : null}
    </Box>
  );
};

const AuditIdCell = ({ audit }) => {
  const [framework, setFramework] = useState({ status: "loading", names: [] });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!audit?.auditId) {
      setFramework({ status: "ready", names: [] });
      return undefined;
    }
    let active = true;
    setFramework({ status: "loading", names: [] });
    loadFrameworkNames(audit).then(
      (names) => {
        if (active) setFramework({ status: "ready", names });
      },
      () => {
        if (active) setFramework({ status: "failed", names: [] });
      }
    );
    return () => {
      active = false;
    };
  }, [audit, attempt]);

  let frameworkText = framework.names.join(", ") || "Not selected";
  if (framework.status === "loading") frameworkText = "Loading…";
  if (framework.status === "failed") frameworkText = "Unavailable";

  return (
    <Tooltip
      placement="top"
      disableInteractive
      onOpen={framework.status === "failed" ? () => setAttempt((n) => n + 1) : undefined}
      title={
        <Box sx={{ textAlign: "left" }}>
          <Box component="span" sx={{ display: "block", fontWeight: 700 }}>
            {audit.auditCode}
          </Box>
          <Box component="span" sx={{ display: "block" }}>
            Framework: {frameworkText}
          </Box>
        </Box>
      }
    >
      <Box
        component="span"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <FrameworkMark name={framework.names[0]} />
        <Typography noWrap sx={{ ...bodyTextSx, minWidth: 0 }}>
          {audit.auditCode}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const OwnerCell = ({ email }) =>
  email ? (
    <CellTip title={email}>
      <Typography sx={wrapTextSx}>{email}</Typography>
    </CellTip>
  ) : (
    <Typography sx={bodyTextSx}>—</Typography>
  );

const OWNER_TIP = "User accountable for the assessment, selected when it is created.";

const readinessText = (value) => {
  const pct = scorePercent(value);
  return pct == null ? "Not yet scored" : `${Math.round(pct)}%`;
};

const manualText = (progress) =>
  progress?.total == null ? "—" : `${progress.decided || 0}/${progress.total} decided`;

const activeColSx = {
  id: { width: "11%" },
  name: { width: "15%" },
  owner: { width: "15%" },
  readiness: { width: "11%" },
  manual: { width: "11%" },
  days: { width: "8%" },
  status: { width: "9%" },
  actions: { width: "20%" },
};

export const ActiveAuditsTable = ({
  rows,
  total,
  page,
  rowsPerPage,
  loading,
  error,
  onPageChange,
  onRowsPerPageChange,
  onOpenAudit,
  onEdit,
  onComplete,
  onCancel,
  onDelete,
  onActivity,
  onLaunch,
  query,
  onQueryChange,
  emptyMessage = "No in-progress assessments match the current filters.",
  orderBy,
  order,
  onSort,
}) => {
  const totalCount = Number.isFinite(Number(total)) ? Number(total) : rows.length;

  return (
  <Box sx={{ width: "100%", mb: 3, boxSizing: "border-box" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 1.75,
        flexWrap: "wrap",
      }}
    >
      <Typography sx={{ fontWeight: 700, color: AH_TEXT, fontSize: "1rem", flexShrink: 0 }}>
        Assessment Details
      </Typography>
      <TextField
        size="small"
        value={query}
        onChange={(event) => onQueryChange(event.target.value)}
        placeholder="Search by ID, Name or Owner"
        aria-label="Search Assessment details"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
            </InputAdornment>
          ),
        }}
        sx={{
          ...searchFieldSx,
          flex: { xs: "1 1 160px", sm: "0 0 auto" },
          width: { xs: "auto", sm: 260 },
          minWidth: { xs: 0, sm: 260 },
        }}
      />
    </Box>
    <Box sx={{ border: `1px solid ${AH_BORDER}`, borderRadius: "12px", backgroundColor: AH_WHITE, overflow: "hidden" }}>
      <TableContainer sx={{ overflowX: "hidden" }}>
        <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headSx, ...activeColSx.id }}>
                <HeaderTip sortField="id" orderBy={orderBy} order={order} onSort={onSort} label="ID" title="Unique reference code assigned to the assessment when it is created." />
              </TableCell>
              <TableCell sx={{ ...headSx, ...activeColSx.name }}>
                <HeaderTip sortField="name" orderBy={orderBy} order={order} onSort={onSort} label="ASSESSMENT NAME" title="Name given to the assessment to identify its purpose." />
              </TableCell>
              <TableCell sx={{ ...headSx, ...activeColSx.owner }}>
                <HeaderTip sortField="owner" orderBy={orderBy} order={order} onSort={onSort} label="ASSESSMENT OWNER" title={OWNER_TIP} />
              </TableCell>
              <TableCell sx={{ ...headSx, ...activeColSx.readiness }}>
                <HeaderTip label="READINESS SCORE" title='Current compliance score across all accounts and frameworks in this assessment, including manual control decisions. Shows "Not yet scored" until a score is available.' />
              </TableCell>
              <TableCell sx={{ ...headSx, ...activeColSx.manual }}>
                <HeaderTip sortField="manual" orderBy={orderBy} order={order} onSort={onSort} label="MANUAL CONTROL ID STATUS" title="Number of manual controls decided so far out of all manual controls in this assessment. Sorts by how many are decided." />
              </TableCell>
              <TableCell sx={{ ...headSx, ...activeColSx.days }}>
                <HeaderTip sortField="days" orderBy={orderBy} order={order} onSort={onSort} label="DAYS REMAINING" title={`Days left until the target completion date of an In-Progress assessment. Shown in amber within ${APPROACHING_COMPLETION_DAYS} days and in red once overdue.`} />
              </TableCell>
              <TableCell sx={{ ...headSx, ...activeColSx.status }}>
                <HeaderTip sortField="status" orderBy={orderBy} order={order} onSort={onSort} label="STATUS" />
              </TableCell>
              <TableCell sx={{ ...headSx, ...activeColSx.actions }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 6, borderBottom: 0, textAlign: "center" }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : error ? (
              <EmptyRows message={error} colSpan={8} />
            ) : rows.length === 0 ? (
              <EmptyRows message={emptyMessage} colSpan={8} />
            ) : (
              rows.map((audit) => {
                const tone = daysRemainingTone(audit);
                const isActive = audit.status === AUDIT_STATUSES.ACTIVE;
                const isDraft = audit.status === AUDIT_STATUSES.DRAFT;
                return (
                  <TableRow key={audit.auditId} hover>
                    <TableCell sx={cellSx}>
                      <AuditIdCell audit={audit} />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: "normal" }}>
                      <CellTip title={audit.name}>
                        <Typography sx={wrapTextSx}>{audit.name}</Typography>
                      </CellTip>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, whiteSpace: "normal" }}>
                      <OwnerCell email={audit.ownerEmail} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <CellTip title={readinessText(audit.auditScore)}>
                        <ReadinessBar value={audit.auditScore} />
                      </CellTip>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <CellTip title={manualText(audit.progress)}>
                        <ManualProgress
                          decided={audit.progress?.decided}
                          total={audit.progress?.total}
                        />
                      </CellTip>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <CellTip title={audit.dueDate ? `Due ${formatShortDate(audit.dueDate)}` : ""}>
                        <Typography
                          sx={{
                            ...bodyTextSx,
                            color: tone === "overdue" ? "#dc2626" : tone === "warn" ? "#d97706" : "#334155",
                          }}
                        >
                          {formatDaysRemaining(audit)}
                        </Typography>
                      </CellTip>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, px: 0.5 }}>
                      <CellTip title={AUDIT_STATUS_LABEL[audit.status] || audit.status}>
                        <AuditStatusChip status={audit.status} />
                      </CellTip>
                    </TableCell>
                    <TableCell sx={{ ...cellSx, ...activeColSx.actions, whiteSpace: "nowrap", overflow: "visible", px: 0.5 }}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", maxWidth: "100%", gap: 0.5 }}>
                        <Tooltip title="Edit Assessment">
                          <IconButton size="small" onClick={() => onEdit(audit)} aria-label={`Edit ${audit.auditCode}`} sx={ACTION_BTN_SX}>
                            <EditOutlinedIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        {isActive ? (
                          <>
                            <Tooltip title="Open this Framework">
                              <IconButton size="small" onClick={() => onOpenAudit(audit)} aria-label={`Open ${audit.auditCode}`} sx={ACTION_BTN_SX}>
                                <OpenInNewOutlinedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <ActivityButton audit={audit} onActivity={onActivity} />
                            <Tooltip title="Cancel Assessment">
                              <IconButton size="small" onClick={() => onCancel(audit)} aria-label={`Cancel ${audit.auditCode}`} sx={DELETE_BTN_SX}>
                                <BlockOutlinedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Complete Assessment">
                              <IconButton size="small" onClick={() => onComplete(audit)} aria-label={`Complete ${audit.auditCode}`} sx={COMPLETE_BTN_SX}>
                                <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : null}
                        {isDraft ? <ActivityButton audit={audit} onActivity={onActivity} /> : null}
                        {isDraft ? (
                          <Tooltip title="Launch Assessment">
                            <IconButton size="small" onClick={() => onLaunch(audit)} aria-label={`Launch ${audit.auditCode}`} sx={COMPLETE_BTN_SX}>
                              <RocketLaunchOutlinedIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        {isDraft ? (
                          <Tooltip title="Delete Assessment">
                            <IconButton size="small" onClick={() => onDelete(audit)} aria-label={`Delete ${audit.auditCode}`} sx={DELETE_BTN_SX}>
                              <i className="mdi mdi-trash-can" style={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <EditAccountTablePagination
        page={page}
        rowsPerPage={rowsPerPage}
        total={totalCount}
        rowsPerPageOptions={[1, 2, 5, 10, 20, 30, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Box>
  </Box>
  );
};

/** Shared columns line up with the In-Progress grid; score sits under readiness, completed on spans manual + days. */
const completedColSx = {
  id: activeColSx.id,
  name: activeColSx.name,
  owner: activeColSx.owner,
  score: activeColSx.readiness,
  completedOn: { width: "19%" },
  status: activeColSx.status,
  report: activeColSx.actions,
};

export const CompletedAuditsTable = ({
  rows,
  total,
  page,
  rowsPerPage,
  loading,
  error,
  onPageChange,
  onRowsPerPageChange,
  onDownload,
  onOpenAudit,
  onActivity,
  orderBy,
  order,
  onSort,
  query,
  onQueryChange,
  emptyMessage = "No completed assessments match the current filters.",
}) => {
  const totalCount = Number.isFinite(Number(total)) ? Number(total) : rows.length;

  return (
  <Box sx={{ width: "100%", boxSizing: "border-box" }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 1.75, flexWrap: "wrap" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: AH_TEXT, fontSize: "1rem", flexShrink: 0 }}>
          Completed & Archived Assessments
        </Typography>
        <TextField
          size="small"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Search by ID, Name or Owner"
          aria-label="Search completed assessments"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            ...searchFieldSx,
            flex: { xs: "1 1 160px", sm: "0 0 auto" },
            width: { xs: "auto", sm: 260 },
            minWidth: { xs: 0, sm: 260 },
          }}
        />
      </Box>
      <Typography sx={{ fontSize: 12, color: AH_MUTED }}>
        Read-only final reports. Completed assessments cannot be reopened.
      </Typography>
    </Box>
    <Box sx={{ border: `1px solid ${AH_BORDER}`, borderRadius: "12px", backgroundColor: AH_WHITE, overflow: "hidden" }}>
      <TableContainer sx={{ overflowX: "hidden" }}>
        <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headSx, ...completedColSx.id }}>
                <HeaderTip sortField="id" orderBy={orderBy} order={order} onSort={onSort} label="ID" title="Unique reference code assigned to the assessment when it is created." />
              </TableCell>
              <TableCell sx={{ ...headSx, ...completedColSx.name }}>
                <HeaderTip sortField="name" orderBy={orderBy} order={order} onSort={onSort} label="ASSESSMENT NAME" title="Name given to the assessment to identify its purpose." />
              </TableCell>
              <TableCell sx={{ ...headSx, ...completedColSx.owner }}>
                <HeaderTip sortField="owner" orderBy={orderBy} order={order} onSort={onSort} label="ASSESSMENT OWNER" title={OWNER_TIP} />
              </TableCell>
              <TableCell sx={{ ...headSx, ...completedColSx.score }}>
                <HeaderTip label="FINAL SCORE" title='Compliance score recorded when the assessment was closed, including manual control decisions. Shows "Not yet scored" if no score was recorded.' />
              </TableCell>
              <TableCell sx={{ ...headSx, ...completedColSx.completedOn }}>
                <HeaderTip sortField="completedOn" orderBy={orderBy} order={order} onSort={onSort} label="COMPLETED ON" title="Date the assessment was completed or cancelled." />
              </TableCell>
              <TableCell sx={{ ...headSx, ...completedColSx.status }}>
                <HeaderTip sortField="status" orderBy={orderBy} order={order} onSort={onSort} label="STATUS" />
              </TableCell>
              <TableCell sx={{ ...headSx, ...completedColSx.report }}>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 6, borderBottom: 0, textAlign: "center" }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : error ? (
              <EmptyRows message={error} colSpan={7} />
            ) : rows.length === 0 ? (
              <EmptyRows message={emptyMessage} colSpan={7} />
            ) : (
              rows.map((audit) => (
                <TableRow key={audit.auditId} hover>
                  <TableCell sx={cellSx}>
                    <AuditIdCell audit={audit} />
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: "normal" }}>
                    <CellTip title={audit.name}>
                      <Typography sx={wrapTextSx}>{audit.name}</Typography>
                    </CellTip>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: "normal" }}>
                    <OwnerCell email={audit.ownerEmail} />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <CellTip title={readinessText(audit.auditScore)}>
                      <ReadinessBar value={audit.auditScore} />
                    </CellTip>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <CellTip title={formatDateTime(audit.lastActivityAt)}>
                      <Typography sx={wrapTextSx}>{formatDateTime(audit.lastActivityAt) || "—"}</Typography>
                    </CellTip>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, px: 0.5 }}>
                    <CellTip title={AUDIT_STATUS_LABEL[audit.status] || audit.status}>
                      <AuditStatusChip status={audit.status} />
                    </CellTip>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, ...completedColSx.report, whiteSpace: "nowrap", overflow: "visible", px: 0.5 }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      {audit.status === AUDIT_STATUSES.COMPLETED ? (
                        <Tooltip title="Open this Framework">
                          <IconButton size="small" onClick={() => onOpenAudit(audit)} aria-label={`Open ${audit.auditCode}`} sx={ACTION_BTN_SX}>
                            <OpenInNewOutlinedIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      <ActivityButton audit={audit} onActivity={onActivity} />
                      <Tooltip title={audit.status === AUDIT_STATUSES.COMPLETED ? "Download report" : "A report is available after the assessment is completed."}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={audit.status !== AUDIT_STATUSES.COMPLETED}
                            onClick={() => onDownload(audit)}
                            aria-label={`Download report ${audit.auditCode}`}
                            sx={ACTION_BTN_SX}
                          >
                            <DownloadOutlinedIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <EditAccountTablePagination
        page={page}
        rowsPerPage={rowsPerPage}
        total={totalCount}
        rowsPerPageOptions={[1, 2, 5, 10, 20, 30, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Box>
  </Box>
  );
};

export const HubEmptyState = () => (
  <Box
    sx={{
      py: 8,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
    }}
  >
    <AssignmentOutlinedIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
    <Typography sx={{ fontSize: 16, fontWeight: 700, color: AH_TEXT, mb: 0.5 }}>
      No assessments yet
    </Typography>
    <Typography sx={{ fontSize: 13, color: AH_MUTED, maxWidth: 420 }}>
      Create an assessment across the accounts and frameworks in scope.
    </Typography>
  </Box>
);
